import PrimaryButton from "@/Component/Common/Forms/PrimaryButton";
import ImageWithFallback from "@/Component/Common/ImageWithFallback";
import Modal from "@/Component/Common/Modal";
import { CloseModalSVG, LoaderSVG } from "@/Component/Common/SVGs/CommonSVG";
import { AppStore } from "@/lib/store";
import CustomFetch from "@/Utils/APIRequest/AxiosInstance";
import { AddDraftCommentAPI, ApproveDraftAPI, CommonS3BucketUploadAPI, CommonUploadAPI, DraftDetailsAPI, GetDraftCommentsAPI } from "@/Utils/APIRequest/Endpoints";
import { CustomToast } from "@/Utils/CustomToast";
import usePost from "@/Utils/Hooks/usePost";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import DraftModalSkeleton from "./DraftModalSkeleton";
import { useLocalDraftSeen } from "@/Utils/Hooks/useLocalSeen";
import { ImageLoaderSVG } from "@/Component/Common/SVGs/AuthSVG";

const DraftModal = (props) => {
    const { draftModalOpen, setDraftModalOpen, refetch, draftInfoData, setUploadDraftModalOpen, setUploadLoader, ManagementDetailData, translations, setOpenSideModal, commentText, setCommentText } = props;
    const [olderVersion, setOlderVersion] = useState(false);
    const userDetails: any = useSelector((state: AppStore) => state.userDetailSlice?.userDetails);
    const [draftVersions, setDraftVersions] = useState<any>([]);
    const [mediaType, setMediaType] = useState('');
    const [thumbnail, setThumbnail] = useState('');
    const [loaderId, setLoaderId] = useState('');
    const [approvedVersionId, setApprovedVersionId] = useState(null);
    const [uploadS3Loader, setUploadS3Loader] = useState(false);
    const [preSignedUrl, setPreSignedUrl] = useState(false);
    const [newFileName, setNewFileName] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileParams, setFileParams] = useState({
        root: "files",
        type: "draft",
        fileName: "",
        contentType: "",
    });

    console.log("ManagementDetailData", draftModalOpen);
    
    // Local seen tracking (without API)
    const { markDraftSeen } = useLocalDraftSeen();

    // Mark draft as seen locally when modal opens
    useEffect(() => {
        if (draftModalOpen?.item?.id && userDetails?.role === 'brand') {
            markDraftSeen(draftModalOpen.item.id);
        }
    }, [draftModalOpen?.item?.id, userDetails?.role, markDraftSeen]);

    const nextDrafts = draftModalOpen?.draftInfoData?.drafts?.filter((item: any) => item?.id !== draftModalOpen?.item?.id && item?.status === "pending");


    // Add Comment Post API
    const { isPending, mutate } = usePost(`${AddDraftCommentAPI}/${draftModalOpen?.item?.id}`, null, (response) => {
        if (response?.status == 200 || response?.status == 201) {
            commentRefetch();
        }
    }, null);

    // Approve Draft Patch API
    const { isPending: approvedPending, mutate: approvedMutate } = usePost(`${ApproveDraftAPI}/${draftModalOpen?.draftInfoData?.id}`, null, (response) => {
        if (response?.status == 200 || response?.status == 201) {
            setApprovedVersionId(draftModalOpen?.draftInfoData?.id);
            setDraftModalOpen(null)
            refetch();
            setOpenSideModal?.((prev) => (prev ? { ...prev, showDraftModal: false } : prev));
        }
    }, 'patch');

    // Upload draft version API
    // const { mutate: mutateUploadFile, isPending: loaderPending } = usePost(CommonUploadAPI, null, (response: any) => {
    //     if (response?.status === 200 || response?.status === 201) {
    //         setUploadDraftModalOpen({
    //             responseData: response?.data,
    //             campaignInfluencerId: draftModalOpen?.draftInfoData?.id,
    //             currentDraftId: draftModalOpen?.item?.id,
    //             JobDetailDataRefetch: refetch,
    //             mediaType: mediaType,
    //             thumbnail: thumbnail,
    //             commentRefetch: commentRefetch,
    //             detailRefetch: DetailRefetch
    //         });
    //     }
    // }, 'POST');


    const { mutate: mutateThumbnailUploadFile, isPending: thumbnailLoader } = usePost(CommonUploadAPI, null, (response: any) => {
        if (response?.status === 200 || response?.status === 201) {
            setThumbnail(response?.data?.fileName);
            setFileParams((prev) => ({
                ...prev,
                fileName: newFileName,
                contentType: selectedFile?.type || "",
            }));
            setPreSignedUrl(true);
            // setUploadDraftModalOpen((prev) => ({ ...prev, thumbnail: response?.data?.fileName }));
        }
    }, 'POST');

    const { data: preSignedUrlData, isLoading: uploadLoader } = useQuery({ queryKey: [CommonS3BucketUploadAPI, fileParams], queryFn: () => CustomFetch({ url: CommonS3BucketUploadAPI, params: { ...fileParams } }, null, false), refetchOnWindowFocus: false, enabled: !!preSignedUrl, select: (data) => data?.data });

    useEffect(() => {
        setUploadLoader(thumbnailLoader || uploadS3Loader)
    }, [thumbnailLoader, uploadS3Loader])

    // Get API
    const { data: draftDetailData, fetchStatus: DetailFetchStatus, refetch: DetailRefetch } = useQuery({ queryKey: [DraftDetailsAPI, draftModalOpen?.item?.id], queryFn: () => CustomFetch({ url: `${DraftDetailsAPI}/${draftModalOpen?.item?.id}` }, null, false), refetchOnWindowFocus: false, enabled: (draftModalOpen?.item?.id) ? true : false, select: (data) => data?.data?.data });

    // Get API
    const { data: commentListData, fetchStatus: commentFetchStatus, refetch: commentRefetch } = useQuery({ queryKey: [GetDraftCommentsAPI, draftModalOpen?.item?.id], queryFn: () => CustomFetch({ url: `${GetDraftCommentsAPI}/${draftModalOpen?.item?.id}` }, null, false), refetchOnWindowFocus: false, enabled: (draftModalOpen?.item?.id) ? true : false, select: (data) => data?.data?.data });

    useEffect(() => {
        if (draftDetailData) {
            setDraftVersions(draftDetailData?.draftVersions?.filter((item: any) => item?.id !== draftDetailData?.draftVersions?.[0]?.id))
        }
    }, [draftDetailData]);

    const isApproved = draftDetailData?.draftVersions?.some((item) => item?.id === draftModalOpen?.item?.id && item?.status === "approved");

    // For Brand - Approve Button Logic
    const isDraftApprovedForBrand = (versionId) => {
        return draftDetailData?.draftVersions?.some((item) => item?.id === versionId && item?.status === 'approved');
    };

    // For Influencer - Upload Button Logic
    const isDraftApprovedForInfluencer = (versionId) => {
        return draftDetailData?.draftVersions?.some((item) => item?.id === versionId && item?.status === 'approved');
    };

    const onFileChange = async (event: any) => {
        setNewFileName('');
        if (event?.target?.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            setMediaType(file?.type);
            setSelectedFile(file);

            const renamed = addTimestampToFileName(file.name);
            setNewFileName(renamed);

            const imageMaxSize = 10 * 1024 * 1024; // 10 MB in bytes
            const videoMaxSize = 500 * 1024 * 1024; // 500 MB in bytes

            const allowedImageTypes = ["image/jpeg", "image/png", "image/webp", "image/svg+xml", "image/heic", "image/heif"];
            const allowedVideoTypes = ["video/mp4", "video/mov", "video/mkv", "video/quicktime", "video/x-msvideo", "video/x-matroska", "video/webm"];

            // Check file type
            if (![...allowedImageTypes, ...allowedVideoTypes].includes(file.type)) {
                CustomToast("error", translations?.draftMediaValidation);
                return;
            }

            // Check file size
            if (allowedImageTypes.includes(file.type) && file.size > imageMaxSize) {
                CustomToast("error", translations?.draftImageValidation.replace(":imageSize", (imageMaxSize / 1024 / 1024)));
                return;
            }

            if (allowedVideoTypes.includes(file.type) && file.size > videoMaxSize) {
                CustomToast("error", translations?.draftVideoValidation.replace(":videoSize", (videoMaxSize / 1024 / 1024)));
                return;
            }

            // let formData: any = new FormData()
            // formData.append('file', file);
            // formData.append('type', 'draft');
            // formData.append('root', "files");
            // mutateUploadFile(formData);


            let body: any = { root: "files", type: "draft", fileName: renamed, contentType: file?.type };


            // mutateUploadFile();

            if (file?.type?.includes('video')) {
                extractVideoThumbnail(file);
            } else {
                setFileParams(body);
                setPreSignedUrl(true);
            }
        }
    };

    const addTimestampToFileName = (fileName: string) => {
        const safeName = fileName.replace(/\s+/g, "_");
        const timestamp = new Date().toISOString();
        const dotIndex = safeName.lastIndexOf(".");
        if (dotIndex === -1) {
            return `${fileName}_${timestamp}`;
        }
        const name = fileName.substring(0, dotIndex);
        const ext = fileName.substring(dotIndex);
        return `${name}_${timestamp}${ext}`;
    }

    useEffect(() => {
        const uploadToS3 = async () => {
            if (!selectedFile || !preSignedUrlData) return;

            setUploadS3Loader(true);

            try {
                const upload = await fetch(preSignedUrlData?.url, {
                    method: "PUT",
                    headers: {
                        "Content-Type": selectedFile.type
                    },
                    body: selectedFile,
                });

                if (upload.ok) {
                    console.log("path", userDetails?.draft_path + newFileName, thumbnail);
                    console.log("draftModalOpen?.draftInfoData?.id", draftModalOpen?.draftInfoData?.id);
                    

                    setUploadDraftModalOpen({
                        responseData: { filepath: (userDetails?.draft_path + newFileName), fileName: newFileName },
                        campaignInfluencerId: draftModalOpen?.draftInfoData?.id,
                        currentDraftId: draftModalOpen?.item?.id,
                        JobDetailDataRefetch: refetch,
                        mediaType: mediaType,
                        thumbnail: thumbnail,
                        commentRefetch: commentRefetch,
                        detailRefetch: DetailRefetch
                    });
                } else {
                    CustomToast("error", "❌ Upload failed");
                }
            } catch (err) {
                console.error(err);
                CustomToast("error", "Upload error occurred");
            } finally {
                setUploadS3Loader(false);
            }
        };
        uploadToS3();
    }, [newFileName, preSignedUrlData, thumbnail]);

    const extractVideoThumbnail = (videoFile) => {
        const video = document.createElement("video");
        const canvas = document.createElement("canvas");
        const ctx: any = canvas.getContext("2d");
        const objectUrl = URL.createObjectURL(videoFile);

        video.src = objectUrl;
        video.crossOrigin = "anonymous";
        video.muted = true;
        video.playsInline = true;
        video.preload = "metadata";
        video.setAttribute("playsinline", "true");
        video.setAttribute("webkit-playsinline", "true");

        let thumbnailExtracted = false;
        let timeoutId: any = null;

        const cleanup = () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };

        const proceedWithoutThumbnail = () => {
            if (thumbnailExtracted) return;
            cleanup();
            setFileParams((prev) => ({
                ...prev,
                fileName: newFileName,
                contentType: selectedFile?.type || "",
            }));
            setPreSignedUrl(true);
        };

        const extractFrame = () => {
            if (thumbnailExtracted) return;
            thumbnailExtracted = true;

            try {
                // Set canvas size to video size
                if (video.videoWidth > 0 && video.videoHeight > 0) {
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;

                    // Draw the video frame onto the canvas
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                    // Convert canvas to an image URL (base64)
                    const thumbnailBase64 = canvas.toDataURL("image/jpeg");

                    console.log("Thumbnail extracted:", thumbnailBase64);
                    base64ToBlob(thumbnailBase64);
                    cleanup();
                } else {
                    // Fallback: try to extract at current time
                    if (video.readyState >= 2) {
                        canvas.width = 640;
                        canvas.height = 360;
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                        const thumbnailBase64 = canvas.toDataURL("image/jpeg");
                        console.log("Thumbnail extracted (fallback):", thumbnailBase64);
                        base64ToBlob(thumbnailBase64);
                        cleanup();
                    } else {
                        proceedWithoutThumbnail();
                    }
                }
            } catch (error) {
                console.error("Error extracting thumbnail:", error);
                proceedWithoutThumbnail();
            }
        };

        // Try to load metadata first
        video.addEventListener("loadedmetadata", () => {
            try {
                // Seek to a small time (0.1s) or use first frame if video is very short
                if (video.duration && video.duration > 0) {
                    const seekTime = Math.min(0.1, video.duration / 2 || 0.1);
                    video.currentTime = seekTime;
                } else {
                    // If duration is not available, try to extract at current position
                    extractFrame();
                }
            } catch (error) {
                console.error("Error seeking video:", error);
                extractFrame();
            }
        }, { once: true });

        video.addEventListener("loadeddata", () => {
            // If metadata wasn't loaded, try to extract frame at current position
            if (video.readyState >= 2 && !thumbnailExtracted) {
                // Small delay to ensure frame is ready
                setTimeout(() => {
                    if (!thumbnailExtracted) {
                        extractFrame();
                    }
                }, 100);
            }
        }, { once: true });

        video.addEventListener("seeked", () => {
            extractFrame();
        }, { once: true });

        // Handle video errors - fallback to proceed without thumbnail
        video.addEventListener("error", (error) => {
            console.error("Error loading video: ", error);
            proceedWithoutThumbnail();
        }, { once: true });

        // Fallback timeout - if video doesn't load in 5 seconds, proceed without thumbnail
        timeoutId = setTimeout(() => {
            if (!thumbnailExtracted) {
                if (video.readyState >= 2) {
                    extractFrame();
                } else {
                    console.warn("Video thumbnail extraction timeout, proceeding without thumbnail");
                    proceedWithoutThumbnail();
                }
            }
        }, 5000);

        // Load the video
        video.load();
    };

    // Convert Base64 to Blob
    const base64ToBlob = (base64Data) => {
        console.log("base64Data", base64Data);

        const byteString = atob(base64Data.split(",")[1]);
        const mimeType = base64Data.split(",")[0].split(":")[1].split(";")[0]; // Get MIME type
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uintArray = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i++) {
            uintArray[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([uintArray], { type: mimeType });

        // Convert Blob to File
        const file = new File([blob], `thumbnail_${Date.now()}.jpg`, { type: mimeType });

        // Upload file
        let formData: any = new FormData();
        formData.append('file', file);
        formData.append('type', 'draft');
        formData.append('root', "files");

        if (!thumbnailLoader) { mutateThumbnailUploadFile(formData) }
    };

    const handleChange = (e) => {
        // return
        setCommentText(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.ctrlKey && e.key === "Enter") {
            e.preventDefault(); // Prevents adding a new line
            handleAddComment();
        }
    };

    const handleAddComment = () => {
        if (!commentText.trim()) {
            CustomToast("error", "Comment cannot be empty");
            return;
        }
        const obj: any = { comment: commentText };
        mutate(obj);
        setCommentText("");
    }

    const handleApproveDraft = (versionId) => {
        let obj: any = { draft_id: draftModalOpen?.item?.id, draft_version_id: versionId };
        setLoaderId(versionId);
        approvedMutate(obj);
    }

    const dangerousTags = [
        'script',
        'style',
        'iframe',
        'embed',
        'object',
        'img',
        'video',
        'audio',
        'source',
        'link'
    ];

    const sanitizeHtml = (html) => {
        if (!html) return '';
        let result = html;
        dangerousTags.forEach(tag => {
            result = result.replace(new RegExp(`<${tag}[\\s\\S]*?>[\\s\\S]*?<\\/${tag}>`, 'gi'), '');
            result = result.replace(new RegExp(`<${tag}[^>]*?\\/?>`, 'gi'), '');
        });
        return result;
    };

    const formatHtml = (text) => {
        let sanitized = sanitizeHtml(text);
        // Convert URLs to clickable anchor tags
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const formattedText = sanitized?.replace(urlRegex, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${url}</a>`)?.replace(/\n/g, "<br/>"); // Preserve line breaks
        return formattedText;
    };


    return (
        <Modal modalSize="xl:w-2/4 md:w-2/3 w-full" modal={draftModalOpen} setModal={setDraftModalOpen} childMainClass="max-h-[90vh] overflow-y-auto">
            <div onClick={() => setDraftModalOpen(null)} className='absolute -top-2 -right-2 cursor-pointer'>
                <CloseModalSVG customClass="max-w-6 max-h-6" />
            </div>
            {DetailFetchStatus === 'fetching' ? <DraftModalSkeleton translations={translations} /> :
                <div className="grid md:grid-cols-2 gap-4 md:divide-x-[1px] max-md:divide-y-[1px] divide-lightGray">
                    <div>
                        <h3 className="text-lg font-bold mb-2">{translations?.draft}</h3>
                        <div className="aspect-square bg-lightGray/50">
                            {draftDetailData?.draftVersions?.[0].type == 'video' ?
                                <video className='w-full h-full object-contain' src={`${userDetails?.draft_path}${draftDetailData?.draftVersions?.[0].draft_name}`} controls /> :
                                <ImageWithFallback src={`${userDetails?.draft_path}${draftDetailData?.draftVersions?.[0].draft_name}`} alt="Profile 1" className='w-full h-full object-contain' width={375} height={375} />
                            }
                        </div>
                        <div className="flex items-center justify-between mt-3">
                            <span className="font-medium text-sm">{moment.utc(draftDetailData?.draftVersions?.[0].created_at).local().format('DD MMM YYYY')} {translations?.at} {moment.utc(draftDetailData?.draftVersions?.[0].created_at).local().format('h:mm A')}</span>
                            {draftDetailData?.draftVersions?.length > 1 &&
                                <span className="font-medium cursor-pointer hover:text-primary hover:underline" onClick={() => setOlderVersion(!olderVersion)}>{olderVersion ? translations?.hide : ''} {translations?.olderVersion}</span>}
                        </div>
                        {(userDetails?.role == 'brand' && ManagementDetailData?.status == 'running') &&
                            <PrimaryButton customClass="mt-3 flex items-center justify-center w-full !rounded gap-1.5" onClick={() => handleApproveDraft(draftDetailData?.draftVersions?.[0].id)} disabled={draftDetailData?.draftVersions?.[0]?.status == 'approved'}>
                                {draftDetailData?.draftVersions?.[0].status == 'pending' ? translations?.approve : translations?.approved}
                                {((loaderId == draftDetailData?.draftVersions?.[0]?.id) && approvedPending) && <LoaderSVG />}
                            </PrimaryButton>
                        }
                        {(userDetails?.role == 'influencer' && ManagementDetailData?.status == 'running') &&
                            ((uploadLoader || uploadS3Loader) ?
                                <label className="border border-primary py-2.5 sm:px-4 px-3 text-base font-medium focus-visible:outline-none hover:shadow-[0_0_0_3px_rgba(185,218,255,1)] transition-shadow bg-primary border-blue text-white mt-3 flex items-center justify-center w-full rounded gap-1.5 cursor-pointer pointer-events-none opacity-75">
                                    {translations?.uploading} <LoaderSVG />
                                </label> :
                                <>
                                    <input type='file' hidden id='versionUpload' accept="image/jpeg,image/png,image/webp,image/svg+xml,image/heic,image/heif,video/mp4,video/quicktime,video/mov,video/mkv,video/x-matroska,video/webm,.jpg,.jpeg,.png,.webp,.svg,.mp4,.mov,.mkv" onChange={onFileChange} />
                                    <label htmlFor="versionUpload" className={`border border-primary py-2.5 sm:px-4 px-3 text-base font-medium focus-visible:outline-none hover:shadow-[0_0_0_3px_rgba(185,218,255,1)] transition-shadow bg-primary border-blue text-white disabled:opacity-75 mt-3 block text-center rounded cursor-pointer ${draftDetailData?.draftVersions?.[0]?.status == 'approved' ? 'pointer-events-none opacity-75' : ''}`}>
                                        {translations?.uploadNewVersion}
                                    </label>
                                </>
                            )
                        }
                        {nextDrafts?.length > 0 &&
                            <>
                                <h3 className="text-xl font-bold mb-3 mt-4">{translations?.nextDraft}</h3>
                                <div className='grid grid-cols-3 gap-4 mt-2.5'>
                                    {nextDrafts?.map((draft, index) => (
                                        <div className='aspect-square bg-gray mb-1 cursor-pointer' onClick={() => setDraftModalOpen({ item: draft, draftInfoData: draftModalOpen?.draftInfoData })} key={index}>
                                            {/* <ImageWithFallback src={`${userDetails?.draft_path}${draft?.draftVersions?.[0].draft_name}`} alt="Profile 1" className='w-full h-full object-contain' width={125} height={125} /> */}
                                            <ImageWithFallback src={`${userDetails?.draft_path}${draft?.draftVersions?.[0].type == 'video' ? draft?.draftVersions?.[0]?.thumbnail : draft?.draftVersions?.[0]?.draft_name}`} alt="Profile 1" className='w-full h-full object-cover' height={64} width={64} />
                                        </div>
                                    ))}
                                </div>
                            </>
                        }
                    </div>
                    {!olderVersion ?
                        <div className="md:pl-4 max-md:pt-4 flex flex-col">
                            <h3 className="text-lg font-bold mb-5">{translations?.allComments}</h3>
                            <div className="overflow-y-auto flex-1 h-[450px] max-h-[450px] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray/10 [&::-webkit-scrollbar-thumb]:bg-tertiary/50">
                                {commentListData?.map((comment, index) => (
                                    <div className="flex items-start gap-2.5 mb-4" key={index}>
                                        <div className="min-w-12 h-12 aspect-square bg-gray rounded-full overflow-hidden">
                                            <ImageWithFallback src={comment?.fromUser?.image} alt="Profile 1" className='w-full h-full object-cover' width={48} height={48} />
                                        </div>
                                        <div className="">
                                            <h3 className="text-base font-medium">{comment?.fromUser?.role == 'influencer' ? comment?.fromUser?.display_name : comment?.fromUser?.first_name + ' ' + comment?.fromUser?.last_name} <span className="capitalize">({comment?.fromUser?.role == 'brand' ? comment?.fromUser?.role : comment?.fromUser?.role == 'admin' ? 'Connector Club' : 'Content Creator'})</span></h3>
                                            <span className="text-xs">{moment.utc(comment.created_at).local().format('DD MMM YYYY')} {translations?.at} {moment.utc(comment.created_at).local().format('h:mm A')}</span>
                                            {/* <p className="mt-2 text-sm break-all">{comment?.comment}</p> */}
                                            <p className="text-sm" dangerouslySetInnerHTML={{ __html: formatHtml(comment?.comment) }}></p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {ManagementDetailData?.status == 'running' &&
                                <div className="border border-lightGray rounded p-2.5 flex items-end gap-1.5 mt-5">
                                    <div className="w-full flex gap-1.5">
                                        <textarea placeholder={translations?.commentPlaceholder} name="comment" className="w-full placeholder:text-tertiary text-black flex-1 focus:outline-none font-medium resize-none [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:rounded-full [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray/60 [&::-webkit-scrollbar-thumb]:bg-lightGray [&::-webkit-scrollbar-track]:rounded-full text-sm" rows={2} onKeyDown={handleKeyDown} onChange={handleChange} value={commentText}></textarea>
                                        <PrimaryButton customClass="flex items-center justify-center !rounded !bg-primary/10 !border-primary/10 !py-1.5 gap-1.5" buttonType="submit" onClick={handleAddComment}>
                                            {translations?.comment}
                                            {isPending && <ImageLoaderSVG />}
                                        </PrimaryButton>
                                    </div>
                                </div>}
                        </div>
                        :
                        <div className="flex flex-col md:pl-4 max-md:pt-4">
                            <h3 className="text-xl font-bold mb-2">{translations?.draftVersionHistory}</h3>
                            <div className="flex flex-col gap-5">
                                {draftVersions?.map((version, index) => (
                                    <div className="bg-gray relative group" key={index}>
                                        <div className="absolute top-3 right-3 flex items-center justify-center bg-black/30 rounded py-1 px-2 text-white text-sm">{moment.utc(version.created_at).local().format('DD MMM YYYY')} {translations?.at} {moment.utc(version.created_at).local().format('h:mm A')}</div>
                                        {version.type == 'video' ?
                                            <video className='w-full h-full object-contain' src={`${userDetails?.draft_path}${version.draft_name}`} controls /> :
                                            <ImageWithFallback src={`${userDetails?.draft_path}${version.draft_name}`} alt="Profile 1" className='w-full h-full object-contain' width={375} height={375} />
                                        }
                                        {(userDetails?.role == 'brand' && ManagementDetailData?.status == 'running') &&
                                            <PrimaryButton customClass="mt-3 flex items-center justify-center !rounded gap-1.5 absolute bottom-3 left-0 right-0 max-w-48 mx-auto invisible group-hover:visible" buttonType="button" onClick={() => handleApproveDraft(version?.id)} disabled={draftDetailData?.draftVersions?.[0]?.status == 'approved'}>
                                                {version?.status == 'pending' ? translations?.approve : translations?.approved}
                                                {((loaderId == version?.id) && approvedPending) && <LoaderSVG />}
                                            </PrimaryButton>
                                        }
                                    </div>
                                ))}
                            </div>
                        </div>
                    }
                </div>
            }
        </Modal>
    )
}

export default DraftModal
